import { useState, useEffect } from 'react'

export interface CompanyInfoItem {
  value: string
  category: string
  description: string
  updated_at: string
}

export interface CompanyInfo {
  [key: string]: CompanyInfoItem
}

interface UseCompanyInfoReturn {
  companyInfo: CompanyInfo | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  getByKey: (key: string) => string
  getByCategory: (category: string) => Record<string, string>
}

/**
 * Custom hook to fetch and manage company information
 * Provides easy access to company contact details, address, social links, etc.
 * 
 * @param category - Optional category filter (contact, social, business_hours, address)
 * @returns Object with company info, loading state, error, and helper functions
 * 
 * @example
 * const { companyInfo, loading, getByKey } = useCompanyInfo()
 * const email = getByKey('email_primary')
 */
export function useCompanyInfo(category?: string): UseCompanyInfoReturn {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const url = category 
        ? `/api/company-info.php?category=${category}`
        : '/api/company-info.php'
      
      const response = await fetch(url)
      const data = await response.json()
      
      if (data.success) {
        setCompanyInfo(data.data)
      } else {
        setError(data.message || 'Failed to fetch company information')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCompanyInfo()
  }, [category])

  /**
   * Get a single company info value by key
   * @param key - The key to look up
   * @returns The value or empty string if not found
   */
  const getByKey = (key: string): string => {
    return companyInfo?.[key]?.value || ''
  }

  /**
   * Get all company info items for a specific category
   * @param cat - The category to filter by
   * @returns Object with key-value pairs for the category
   */
  const getByCategory = (cat: string): Record<string, string> => {
    if (!companyInfo) return {}
    
    return Object.entries(companyInfo)
      .filter(([, item]) => item.category === cat)
      .reduce((acc, [key, item]) => {
        acc[key] = item.value
        return acc
      }, {} as Record<string, string>)
  }

  return {
    companyInfo,
    loading,
    error,
    refetch: fetchCompanyInfo,
    getByKey,
    getByCategory
  }
}
